// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the playlists people had, turned into Khipus once (owner,
 * 2026-11-03: playlists retire for Khipus; "convert once").
 *
 * On a page load, `convertLegacyPlaylists` reads the playlists this browser
 * kept (`sos-playlists.v1`, with its deleted markers) and, signed in, the
 * copy the account kept (`GET /api/account/playlists`, once per account per
 * browser), keeps the newer copy of each, and makes each a story
 * (`playlistToKhipu`, a stable id per playlist, so two devices make one
 * Khipu). Then:
 *
 * - a plan that keeps Khipus saves them to My Khipus, never over a Khipu
 *   already kept under that id (another device converted it, and it may have
 *   been edited since);
 * - otherwise (signed out, Free, Plus), or when saving fails, they wait in
 *   this browser (`ayni-playlist-khipus.v1`) and Browse → Khipus offers
 *   each to open in the editor (Download keeps it) or to put away. The next
 *   load on a plan that keeps Khipus saves the waiting ones.
 *
 * The browser's playlists are then removed, and the account's copy is
 * deleted (`DELETE /api/account/playlists`). Old exported playlist files
 * take the same road (`convertPlaylistFile`). Nothing here adds playlists
 * back: there is no way to make a new one.
 */

import type { Khipu } from './khipu'
import { fnv1a64, newerPlaylist, playlistToKhipu, readLegacyPlaylists, type LegacyPlaylist } from './khipuCollect'
import { listKeptKhipus, saveKhipu } from './khipuStore'

/** Where browsers kept playlists until 2026-11-03, and their deleted markers. */
export const LEGACY_PLAYLISTS_KEY = 'sos-playlists.v1'
export const LEGACY_DELETED_KEY = 'sos-playlists-deleted.v1'
/** Converted playlists not kept on an account, offered in Browse → Khipus. */
export const WAITING_KEY = 'ayni-playlist-khipus.v1'
/** The accounts (a hash of the email, never the email) whose kept playlists this browser already asked for. */
export const CHECKED_KEY = 'ayni-playlists-checked.v1'

export interface ConvertOptions {
  lang: string
  nowMs: number
  titleOf: (datasetId: string) => string | null
  /** The name of a playlist that had none. */
  untitled: string
  /** The signed-in account's email, or null signed out. */
  email: string | null
  /** The plan keeps Khipus on the account. */
  canKeep: boolean
  storage?: Storage | null
  fetchImpl?: typeof fetch
}

export interface ConvertResult {
  /** Saved to My Khipus now. */
  saved: number
  /** Waiting in this browser, in Browse → Khipus. */
  waiting: number
}

function defaultStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

function readJson(storage: Storage | null, key: string): unknown {
  try {
    const raw = storage?.getItem(key)
    return raw ? JSON.parse(raw) as unknown : null
  } catch {
    return null
  }
}

function write(storage: Storage | null, key: string, value: unknown): void {
  try {
    if (value === null) storage?.removeItem(key)
    else storage?.setItem(key, JSON.stringify(value))
  } catch {
    // Storage full or switched off: nothing more to do here.
  }
}

/** The converted playlists waiting in this browser. */
export function waitingKhipus(storage: Storage | null = defaultStorage()): Khipu[] {
  const raw = readJson(storage, WAITING_KEY)
  if (!Array.isArray(raw)) return []
  return raw.filter((k): k is Khipu => !!k && typeof k === 'object' && typeof (k as Khipu).id === 'string' && Array.isArray((k as Khipu).path?.steps))
}

const WAITING_EVENT = 'ayni-playlist-khipus:change'
const waitingTarget: EventTarget = typeof window === 'undefined' ? new EventTarget() : window

/** The waiting list changed (converted, kept, or put away). */
export function onWaitingChanged(listener: () => void): () => void {
  waitingTarget.addEventListener(WAITING_EVENT, listener)
  return () => waitingTarget.removeEventListener(WAITING_EVENT, listener)
}

function writeWaiting(list: Khipu[], storage: Storage | null): void {
  const before = storage?.getItem(WAITING_KEY) ?? null
  write(storage, WAITING_KEY, list.length ? list : null)
  if ((storage?.getItem(WAITING_KEY) ?? null) !== before) waitingTarget.dispatchEvent(new Event(WAITING_EVENT))
}

/** Put one waiting Khipu away (it was kept, downloaded, or is not wanted). */
export function putAwayWaiting(id: string, storage: Storage | null = defaultStorage()): void {
  writeWaiting(waitingKhipus(storage).filter((k) => k.id !== id), storage)
}

/** The ids in a list of deleted markers (`{ id, deletedAt }`). */
function deletedIdsIn(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  return raw.map((d) => (d && typeof d === 'object' ? (d as { id?: unknown }).id : null)).filter((id): id is string => typeof id === 'string')
}

/** Each id once, the later entry winning. */
function byId(list: Khipu[]): Khipu[] {
  const map = new Map<string, Khipu>()
  for (const k of list) map.set(k.id, k)
  return [...map.values()]
}

/**
 * Keep `fresh` (and what was waiting) on the account where the plan keeps
 * Khipus; whatever is not kept waits in this browser.
 */
async function keepOrWait(fresh: Khipu[], opts: ConvertOptions, storage: Storage | null, fetchImpl: typeof fetch): Promise<ConvertResult> {
  const candidates = byId([...waitingKhipus(storage), ...fresh])
  if (!opts.canKeep || candidates.length === 0) {
    writeWaiting(candidates, storage)
    return { saved: 0, waiting: candidates.length }
  }
  const kept = await listKeptKhipus(fetchImpl)
  if (!kept.ok) {
    writeWaiting(candidates, storage)
    return { saved: 0, waiting: candidates.length }
  }
  const have = new Set(kept.value.items.map((i) => i.id))
  const waiting: Khipu[] = []
  let saved = 0
  for (const k of candidates) {
    if (have.has(k.id)) continue
    const result = await saveKhipu(k, [], fetchImpl)
    if (result.ok) saved++
    else waiting.push(k)
  }
  writeWaiting(waiting, storage)
  return { saved, waiting: waiting.length }
}

function toKhipus(list: LegacyPlaylist[], opts: ConvertOptions): Khipu[] {
  return list.map((p) => playlistToKhipu(p, { lang: opts.lang, nowMs: opts.nowMs, titleOf: opts.titleOf, untitled: opts.untitled }))
}

/**
 * Turn the playlists this browser and the signed-in account kept into
 * Khipus. Safe on every load: once they are gone it only retries the
 * waiting ones on a plan that keeps Khipus.
 */
export async function convertLegacyPlaylists(opts: ConvertOptions): Promise<ConvertResult> {
  const storage = opts.storage === undefined ? defaultStorage() : opts.storage
  const fetchImpl = opts.fetchImpl ?? fetch
  // Playlist ids are random, so a deleted id never comes back: a deletion from either side wins.
  const deletedIds = new Set(deletedIdsIn(readJson(storage, LEGACY_DELETED_KEY)))
  const found = new Map<string, LegacyPlaylist>()
  for (const p of readLegacyPlaylists(readJson(storage, LEGACY_PLAYLISTS_KEY))) found.set(p.id, p)

  // The account's copy, once per account in this browser.
  const account = opts.email ? fnv1a64(`account:${opts.email.trim().toLowerCase()}`) : null
  const checked = readJson(storage, CHECKED_KEY)
  const checkedList = Array.isArray(checked) ? checked.filter((x): x is string => typeof x === 'string') : []
  let accountHad = false
  let accountRead = false
  if (account && !checkedList.includes(account)) {
    try {
      const res = await fetchImpl('/api/account/playlists', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      if (res.ok) {
        accountRead = true
        const doc = await res.json() as { deleted?: unknown }
        for (const id of deletedIdsIn(doc?.deleted)) deletedIds.add(id)
        for (const p of readLegacyPlaylists(doc)) {
          accountHad = true
          const local = found.get(p.id)
          found.set(p.id, local ? newerPlaylist(local, p) : p)
        }
      }
    } catch {
      // Not now; the next load asks again.
    }
  }
  for (const id of deletedIds) found.delete(id)

  const result = await keepOrWait(toKhipus([...found.values()], opts), opts, storage, fetchImpl)
  write(storage, LEGACY_PLAYLISTS_KEY, null)
  write(storage, LEGACY_DELETED_KEY, null)
  if (account && accountRead) {
    // Converted (kept, or waiting here): the account's copy goes.
    if (accountHad) await fetchImpl('/api/account/playlists', { method: 'DELETE', credentials: 'same-origin', headers: { Accept: 'application/json' } }).catch(() => null)
    write(storage, CHECKED_KEY, [...checkedList, account].slice(-20))
  }
  return result
}

/** Whether a file's JSON is an old playlists export (`ayni-playlists.json`). */
export function isPlaylistFile(raw: unknown): boolean {
  return readLegacyPlaylists(raw).length > 0
}

/** An old playlists export, opened: its playlists become Khipus, kept or waiting as above. */
export async function convertPlaylistFile(raw: unknown, opts: ConvertOptions): Promise<ConvertResult & { found: number }> {
  const list = readLegacyPlaylists(raw)
  const storage = opts.storage === undefined ? defaultStorage() : opts.storage
  const result = await keepOrWait(toKhipus(list, opts), opts, storage, opts.fetchImpl ?? fetch)
  return { ...result, found: list.length }
}
